import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonService from '../../../services/common.service';
import { toast } from 'react-toastify';
import '../../../assets/styles/approve_goods_receipt_list.scss';

export default function ApproveGoodsReceiptList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [acceptGRN, setAcceptGRN] = useState(null);
  const [grnRecords, setGrnRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [acceptanceForm, setAcceptanceForm] = useState({
    decision: '',
    approverName: '',
    designation: '',
    approvalDate: new Date().toISOString().split('T')[0],
    approvalNotes: '',
    followUpAction: '',
    rejectionReason: ''
  });

  // Transform backend GRN data to frontend format
  const transformGRNData = (grn) => {
    // Parse quality inspection checklist
    let qualityChecklist = {};
    if (grn.quality_inspection_checklist) {
      try {
        qualityChecklist = typeof grn.quality_inspection_checklist === 'string'
          ? JSON.parse(grn.quality_inspection_checklist)
          : grn.quality_inspection_checklist;
      } catch (e) {
        console.error('Error parsing quality_inspection_checklist:', e);
      }
    }

    // Build discrepancies array
    const discrepancies = [];
    if (grn.shortage_detected) discrepancies.push('Shortage');
    if (grn.damage_detected) discrepancies.push('Damage');
    if (grn.quality_issue) discrepancies.push('Quality Issues');

    return {
      id: grn.id,
      grnNumber: grn.grn_number || 'N/A',
      status: grn.status || 'Pending Approval',
      poNumber: grn.po_number || (grn.purchase_order_id ? `PO-${grn.purchase_order_id}` : 'N/A'),
      grnDate: grn.grn_date || null,
      receivedBy: grn.received_by_name || 'N/A',
      invoice: grn.invoice_number || 'N/A',
      supplier: grn.supplier_name || 'N/A',
      totalAccepted: grn.total_qty_accepted || 0,
      totalRejected: grn.total_qty_rejected || 0,
      warehouse: grn.warehouse_location || 'N/A',
      discrepancies: discrepancies,
      // Store full GRN data for details modal
      fullData: grn,
      qualityChecklist: qualityChecklist
    };
  };

  // Fetch GRNs for seller
  const fetchGRNs = async () => {
    try {
      setLoading(true);
      const response = await CommonService.get('/grn/seller/my-grns');
      if (response && response.success && response.data) {
        const transformedGRNs = response.data.map(transformGRNData);
        setGrnRecords(transformedGRNs);
      } else {
        console.error('Failed to fetch GRNs:', response);
        const errorMessage = response?.error || response?.message || 'Failed to fetch GRNs';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error fetching GRNs:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Error fetching GRNs';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGRNs();
  }, []);

  // Sample GRN data (kept for reference, but will be replaced by API data)
  const sampleGrnRecords = [
    {
      id: 1,
      grnNumber: 'GRN-2025-ABC123',
      status: 'Pending Approval',
      poNumber: 'PO-2025-001234',
      grnDate: '2025-12-01',
      receivedBy: 'Suresh Kumar',
      invoice: 'INV-2025-001234',
      supplier: 'ABC Manufacturing Ltd.',
      totalAccepted: 123,
      totalRejected: 2,
      warehouse: 'Main Warehouse - Bangalore',
      discrepancies: ['Damage']
    },
    {
      id: 2,
      grnNumber: 'GRN-2025-DEF456',
      status: 'Approved',
      poNumber: 'PO-2025-001235',
      grnDate: '2025-12-02',
      receivedBy: 'Amit Patel',
      invoice: 'INV-2025-002345',
      supplier: 'XYZ Components Pvt Ltd',
      totalAccepted: 50,
      totalRejected: 0,
      warehouse: 'Secondary Warehouse - Mumbai',
      discrepancies: []
    },
    {
      id: 3,
      grnNumber: 'GRN-2025-GHI789',
      status: 'Rejected',
      poNumber: 'PO-2025-001236',
      grnDate: '2025-11-30',
      receivedBy: 'Vikram Singh',
      invoice: 'INV-2025-003456',
      supplier: 'Tech Solutions Ltd',
      totalAccepted: 0,
      totalRejected: 60,
      warehouse: 'Regional Hub - Delhi',
      discrepancies: ['Shortage', 'Damage', 'Quality Issues']
    }
  ];

  const statusOptions = ['All Status', 'Pending Approval', 'Approved', 'Partially Approved', 'Rejected'];

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'Pending Approval': 'agrl-status-pending',
      'Approved': 'agrl-status-approved',
      'Partially Approved': 'agrl-status-partial',
      'Rejected': 'agrl-status-rejected'
    };
    return statusMap[status] || 'agrl-status-pending';
  };

  const filteredGRNs = grnRecords.filter(grn => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const searchFields = [
      grn.grnNumber,
      grn.poNumber,
      grn.supplier,
      grn.invoice,
      grn.warehouse
    ].filter(Boolean);

    const matchesSearch = normalizedQuery === '' 
      ? true 
      : searchFields.some(field => field.toLowerCase().includes(normalizedQuery));
    
    const matchesStatus = statusFilter === 'All Status' || grn.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Fetch detailed GRN data with items and inspection details
  const getGRNDetails = async (grnId) => {
    try {
      const response = await CommonService.get(`/grn/${grnId}`);
      if (response && response.success && response.data) {
        return transformGRNDetailsData(response.data);
      }
      return null;
    } catch (error) {
      console.error('Error fetching GRN details:', error);
      toast.error('Failed to fetch GRN details');
      return null;
    }
  };

  // Transform backend GRN details to frontend format
  const transformGRNDetailsData = (grn) => {
    // Parse quality inspection checklist
    let qualityChecklist = {};
    if (grn.quality_inspection_checklist) {
      try {
        qualityChecklist = typeof grn.quality_inspection_checklist === 'string'
          ? JSON.parse(grn.quality_inspection_checklist)
          : grn.quality_inspection_checklist;
      } catch (e) {
        console.error('Error parsing quality_inspection_checklist:', e);
      }
    }

    // Transform items
    const items = (grn.items || []).map(item => ({
      sku: item.sku_item_code || 'N/A',
      description: item.description || 'N/A',
      hsn: item.hsn || 'N/A',
      uom: item.uom || 'N/A',
      ordered: parseFloat(item.qty_ordered) || 0,
      received: parseFloat(item.qty_received) || 0,
      accepted: parseFloat(item.qty_accepted) || 0,
      rejected: parseFloat(item.qty_rejected) || 0,
      batch: item.batch_lot || 'N/A',
      mfgDate: item.mfg_date || 'N/A',
      expDate: item.exp_date || 'N/A',
      condition: item.condition || 'Good',
      remarks: item.remarks || ''
    }));

    // Build discrepancies object
    const discrepancies = {};
    if (grn.shortage_detected) {
      discrepancies.shortageDetected = true;
      discrepancies.shortageDescription = grn.defect_issue_description || 'Shortage detected';
    }
    if (grn.damage_detected) {
      discrepancies.damageDetected = true;
      discrepancies.damageDescription = grn.defect_issue_description || 'Damage detected';
    }
    if (grn.quality_issue) {
      discrepancies.qualityIssue = true;
      discrepancies.qualityIssueDescription = grn.defect_issue_description || 'Quality issue detected';
    }

    return {
      id: grn.id,
      grnNumber: grn.grn_number || 'N/A',
      grnDate: grn.grn_date || null,
      poNumber: grn.po_number || (grn.purchase_order_id ? `PO-${grn.purchase_order_id}` : 'N/A'),
      invoice: grn.invoice_number || 'N/A',
      deliveryChallan: grn.delivery_challan_no || 'N/A',
      status: grn.status || 'Pending Approval',
      supplier: {
        name: grn.supplier_name || 'N/A',
        gstin: grn.supplier_gstin || 'N/A',
        email: grn.contact_person ? `${grn.contact_person.toLowerCase().replace(/\s+/g, '.')}@supplier.com` : 'N/A',
        phone: grn.phone || 'N/A',
        address: grn.address || 'N/A'
      },
      delivery: {
        date: grn.delivery_date || grn.grn_date || null,
        warehouse: grn.warehouse_location || 'N/A',
        receivedBy: grn.received_by_name || 'N/A',
        manifestNumber: grn.manifest_number || 'N/A',
        deliveryChallan: grn.delivery_challan_no || 'N/A',
        invoiceNumber: grn.invoice_number || 'N/A',
        vehicleNumber: grn.vehicle_number || 'N/A',
        driverName: grn.driver_name || 'N/A',
        driverContact: grn.driver_contact || 'N/A',
        lrNumber: grn.lr_number || 'N/A',
        transportCompany: grn.transport_company || 'N/A'
      },
      items: items,
      physicalVerification: {
        packagesExpected: grn.packages_expected || 0,
        packagesReceived: grn.packages_received || 0,
        weightExpected: grn.weight_expected_kg || 0,
        weightReceived: grn.weight_received_kg || 0,
        sealsVerified: grn.seals_verified || false,
        sealNumbers: grn.seal_numbers || '',
        dimensionsVerified: grn.dimensions_verified || false,
        packagingIntegrity: grn.packaging_integrity_ok || false
      },
      qualityInspection: {
        packagingIntact: qualityChecklist.packagingIntact || false,
        correctLabeling: qualityChecklist.correctLabeling || false,
        noVisibleDamage: qualityChecklist.noVisibleDamage || false,
        quantityMatches: qualityChecklist.quantityMatches || false,
        meetsQualityStandards: qualityChecklist.meetsQualityStandards || false,
        documentsComplete: qualityChecklist.allDocumentsComplete || qualityChecklist.documentsComplete || false,
        inspector: grn.inspector_name || 'N/A',
        inspectionDate: grn.inspection_date || grn.grn_date || null,
        defectDescription: grn.defect_issue_description || ''
      },
      discrepancies: discrepancies,
      additionalNotes: grn.additional_notes || '',
      approvalDetails: {
        approvedBy: grn.approved_by || null,
        approvedByDesignation: grn.approved_by_designation || null,
        approvedDate: grn.approved_date || null,
        approvalNotes: grn.approval_notes || null,
        followUpAction: grn.follow_up_action || null
      },
      rejectionDetails: {
        rejectedBy: grn.rejected_by || null,
        rejectedByDesignation: grn.rejected_by_designation || null,
        rejectedDate: grn.rejected_date || null,
        rejectionReason: grn.rejection_reason || null,
        followUpAction: grn.follow_up_action || null
      }
    };
  };

  const handleViewDetails = async (grnId) => {
    const grnDetails = await getGRNDetails(grnId);
    if (grnDetails) {
      setSelectedGRN(grnDetails);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedGRN(null);
  };

  const handleReviewAccept = (grnId) => {
    const grn = grnRecords.find(g => g.id === grnId);
    if (grn) {
      // Close the details modal if it's open
      setIsModalOpen(false);
      setSelectedGRN(null);
      // Open the acceptance modal
      setAcceptGRN(grn);
      setIsAcceptModalOpen(true);
      setAcceptanceForm({
        decision: '',
        approverName: '',
        designation: '',
        approvalDate: new Date().toISOString().split('T')[0],
        approvalNotes: '',
        followUpAction: '',
        rejectionReason: ''
      });
    }
  };

  const handleCloseAcceptModal = () => {
    setIsAcceptModalOpen(false);
    setAcceptGRN(null);
    setAcceptanceForm({
      decision: '',
      approverName: '',
      designation: '',
      approvalDate: new Date().toISOString().split('T')[0],
      approvalNotes: '',
      followUpAction: '',
      rejectionReason: ''
    });
  };

  const handleAcceptanceFormChange = (field, value) => {
    setAcceptanceForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const followUpActionOptions = [
    { value: '', label: 'Select action...' },
    { value: 'RETURN_TO_SUPPLIER', label: 'Return to Supplier' },
    { value: 'REPLACEMENT_REQUIRED', label: 'Replacement Required' },
    { value: 'CREDIT_NOTE', label: 'Issue Credit Note' },
    { value: 'INVESTIGATE', label: 'Investigate Further' },
    { value: 'ACCEPT_WITH_DISCOUNT', label: 'Accept with Discount' },
    { value: 'OTHER', label: 'Other' }
  ];

  // Helper function to get follow-up action label from value
  const getFollowUpActionLabel = (value) => {
    if (!value) return null;
    const option = followUpActionOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const handleSubmitDecision = async (e) => {
    e.preventDefault();
    if (!acceptanceForm.decision || !acceptanceForm.approverName || !acceptanceForm.designation) {
      toast.error('Please fill in all required fields');
      return;
    }
    // Validate approval notes if decision is APPROVE
    if (acceptanceForm.decision === 'APPROVE' && !acceptanceForm.approvalNotes.trim()) {
      toast.error('Please enter approval notes');
      return;
    }
    // Validate follow-up action if decision is PARTIALLY_ACCEPT
    if (acceptanceForm.decision === 'PARTIALLY_ACCEPT' && !acceptanceForm.followUpAction) {
      toast.error('Please select a follow-up action');
      return;
    }
    // Validate rejection reason if decision is REJECT
    if (acceptanceForm.decision === 'REJECT' && !acceptanceForm.rejectionReason.trim()) {
      toast.error('Please enter a rejection reason');
      return;
    }
    // Validate follow-up action if decision is REJECT
    if (acceptanceForm.decision === 'REJECT' && !acceptanceForm.followUpAction) {
      toast.error('Please select a follow-up action');
      return;
    }

    try {
      setSubmitting(true);
      const grnId = acceptGRN.id;
      let response;

      // Get follow-up action value (for partial approve and reject)
      const followUpAction = acceptanceForm.followUpAction || null;

      if (acceptanceForm.decision === 'APPROVE') {
        // Approve GRN
        response = await CommonService.put(`/grn/${grnId}/approve`, {
          approved_by: acceptanceForm.approverName,
          approved_by_designation: acceptanceForm.designation,
          approval_notes: acceptanceForm.approvalNotes || null,
          follow_up_action: followUpAction
        });
      } else if (acceptanceForm.decision === 'PARTIALLY_ACCEPT') {
        // Partially Approve GRN
        response = await CommonService.put(`/grn/${grnId}/partially-approve`, {
          approved_by: acceptanceForm.approverName,
          approved_by_designation: acceptanceForm.designation,
          approval_notes: acceptanceForm.approvalNotes || null,
          follow_up_action: followUpAction
        });
      } else if (acceptanceForm.decision === 'REJECT') {
        // Reject GRN
        response = await CommonService.put(`/grn/${grnId}/reject`, {
          rejected_by: acceptanceForm.approverName,
          rejected_by_designation: acceptanceForm.designation,
          rejection_reason: acceptanceForm.rejectionReason,
          follow_up_action: followUpAction
        });
      }

      if (response && response.success) {
        toast.success(response.message || 'GRN decision submitted successfully');
        handleCloseAcceptModal();
        // Refresh GRN list
        await fetchGRNs();
      } else {
        const errorMessage = response?.error || response?.message || 'Failed to submit decision';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error submitting decision:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Error submitting decision';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="approve-goods-receipt-list">
      <div className="agrl-main-content">
        {/* Header Section */}
        <div className="agrl-header">
          <div className="agrl-header-left">
            <nav aria-label="breadcrumb" className="agrl-breadcrumb">
              <ol className="agrl-breadcrumb-list">
                <li className="agrl-breadcrumb-item">
                  <a href="/" className="agrl-breadcrumb-link">Home</a>
                </li>
                <li className="agrl-breadcrumb-item agrl-breadcrumb-active" aria-current="page">
                  GRN Acceptance & Approval
                </li>
              </ol>
            </nav>
            <h1 className="agrl-main-title">GRN Acceptance & Approval</h1>
            <p className="agrl-subtitle">Review and approve Goods Received Notes</p>
          </div>
          <div className="agrl-header-right">
            <button className="agrl-back-btn" onClick={() => navigate(-1)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
              Back
            </button>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="agrl-search-filter-section">
          <div className="agrl-search-filter-row">
            <div className="agrl-search-wrapper">
              <svg 
                className="agrl-search-icon" 
                xmlns="http://www.w3.org/2000/svg" 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="agrl-search-input"
                placeholder="Search by GRN Number, PO Number, or Supplier Name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="agrl-filter-wrapper">
              <select
                className="agrl-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* GRN Records Section */}
        <div className="agrl-records-section">
          <h2 className="agrl-records-title">GRN Records ({filteredGRNs.length})</h2>
          
          {loading ? (
            <div className="agrl-empty-state">
              <p>Loading GRN records...</p>
            </div>
          ) : filteredGRNs.length === 0 ? (
            <div className="agrl-empty-state">
              <p>No GRN records found matching your search criteria.</p>
            </div>
          ) : (
            <div className="agrl-grn-cards">
              {filteredGRNs.map(grn => (
                <div key={grn.id} className="agrl-grn-card">
                  <div className="agrl-card-header">
                    <div className="agrl-grn-header-left">
                      <h3 className="agrl-grn-number">{grn.grnNumber}</h3>
                      <span className={`agrl-status-badge ${getStatusClass(grn.status)}`}>
                        {grn.status}
                      </span>
                    </div>
                    <div className="agrl-card-actions">
                      <button 
                        className="agrl-action-btn agrl-btn-view"
                        onClick={() => handleViewDetails(grn.id)}
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                        View Details
                      </button>
                      {grn.status === 'Pending Approval' && (
                        <button 
                          className="agrl-action-btn agrl-btn-accept"
                          onClick={() => handleReviewAccept(grn.id)}
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          Review & Accept
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="agrl-card-body">
                    <div className="agrl-details-grid">
                      <div className="agrl-grid-item">
                        <p className="agrl-detail-label">PO Number</p>
                        <p className="agrl-detail-value">{grn.poNumber}</p>
                      </div>
                      <div className="agrl-grid-item">
                        <p className="agrl-detail-label">GRN Date</p>
                        <p className="agrl-detail-value">{formatDate(grn.grnDate)}</p>
                      </div>
                      <div className="agrl-grid-item">
                        <p className="agrl-detail-label">Received By</p>
                        <p className="agrl-detail-value">{grn.receivedBy}</p>
                      </div>
                      <div className="agrl-grid-item">
                        <p className="agrl-detail-label">Invoice</p>
                        <p className="agrl-detail-value">{grn.invoice}</p>
                      </div>
                      <div className="agrl-grid-item">
                        <p className="agrl-detail-label">Supplier</p>
                        <p className="agrl-detail-value">{grn.supplier}</p>
                      </div>
                      <div className="agrl-grid-item">
                        <p className="agrl-detail-label">Total Accepted</p>
                        <p className="agrl-detail-value agrl-value-accepted">{grn.totalAccepted}</p>
                      </div>
                      <div className="agrl-grid-item">
                        <p className="agrl-detail-label">Warehouse</p>
                        <p className="agrl-detail-value">{grn.warehouse}</p>
                      </div>
                      <div className="agrl-grid-item">
                        <p className="agrl-detail-label">Total Rejected</p>
                        <p className="agrl-detail-value agrl-value-rejected">{grn.totalRejected}</p>
                      </div>
                    </div>

                    {grn.discrepancies && grn.discrepancies.length > 0 && (
                      <div className="agrl-discrepancies">
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          className="agrl-discrepancy-icon"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <span className="agrl-discrepancy-text">
                          Discrepancies Reported: {grn.discrepancies.join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* GRN Details Modal */}
      {isModalOpen && selectedGRN && (
        <div className="agrl-modal-overlay" onClick={handleCloseModal}>
          <div
            className="agrl-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="agrl-modal-title"
            aria-describedby="agrl-modal-description"
            tabIndex="-1"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="agrl-modal-close" 
              onClick={handleCloseModal}
              aria-label="Close GRN details"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>

            <div className="agrl-modal-header">
              <div>
                <h2 id="agrl-modal-title" className="agrl-modal-title">GRN Details - {selectedGRN.grnNumber}</h2>
                <p id="agrl-modal-description" className="agrl-modal-subtitle">Complete details of the Goods Received Note</p>
              </div>
            </div>

            <div className="agrl-modal-content">
              {/* Summary Grid */}
              <div className="agrl-summary-grid">
                <div className="agrl-summary-item">
                  <label className="agrl-summary-label">GRN Number</label>
                  <p className="agrl-summary-value agrl-value-mono">{selectedGRN.grnNumber}</p>
                </div>
                <div className="agrl-summary-item">
                  <label className="agrl-summary-label">GRN Date</label>
                  <p className="agrl-summary-value">{formatDate(selectedGRN.grnDate)}</p>
                </div>
                <div className="agrl-summary-item">
                  <label className="agrl-summary-label">PO Number</label>
                  <p className="agrl-summary-value agrl-value-mono">{selectedGRN.poNumber}</p>
                </div>
                <div className="agrl-summary-item">
                  <label className="agrl-summary-label">Invoice Number</label>
                  <p className="agrl-summary-value agrl-value-mono">{selectedGRN.invoice}</p>
                </div>
                <div className="agrl-summary-item">
                  <label className="agrl-summary-label">Delivery Challan</label>
                  <p className="agrl-summary-value agrl-value-mono">{selectedGRN.deliveryChallan}</p>
                </div>
                <div className="agrl-summary-item">
                  <label className="agrl-summary-label">Status</label>
                  <div className="agrl-summary-status">
                    <span className={`agrl-status-badge ${getStatusClass(selectedGRN.status)}`}>
                      {selectedGRN.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Supplier Details */}
              <div className="agrl-section">
                <h3 className="agrl-section-title agrl-section-title-orange">
                  Supplier Details
                </h3>
                <div className="agrl-section-card agrl-card-orange">
                  <div className="agrl-section-grid agrl-grid-4">
                    <div>
                      <label className="agrl-section-label">Supplier Name</label>
                      <p className="agrl-section-value">{selectedGRN.supplier.name}</p>
                    </div>
                    <div>
                      <label className="agrl-section-label">GSTIN</label>
                      <p className="agrl-section-value agrl-value-mono">{selectedGRN.supplier.gstin}</p>
                    </div>
                    {selectedGRN.supplier.email && (
                      <div>
                        <label className="agrl-section-label">Email</label>
                        <p className="agrl-section-value">{selectedGRN.supplier.email}</p>
                      </div>
                    )}
                    {selectedGRN.supplier.phone && (
                      <div>
                        <label className="agrl-section-label">Phone Number</label>
                        <p className="agrl-section-value">{selectedGRN.supplier.phone}</p>
                      </div>
                    )}
                    {selectedGRN.supplier.address && (
                      <div style={{ gridColumn: 'span 4' }}>
                        <label className="agrl-section-label">Address</label>
                        <p className="agrl-section-value">{selectedGRN.supplier.address}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Delivery Information */}
              <div className="agrl-section">
                <h3 className="agrl-section-title agrl-section-title-purple">
                  Delivery Information
                </h3>
                <div className="agrl-section-card agrl-card-purple">
                  <div className="agrl-section-grid agrl-grid-4">
                    <div>
                      <label className="agrl-section-label">Delivery Challan</label>
                      <p className="agrl-section-value agrl-value-mono">{selectedGRN.delivery.deliveryChallan || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="agrl-section-label">Invoice Number</label>
                      <p className="agrl-section-value agrl-value-mono">{selectedGRN.delivery.invoiceNumber || selectedGRN.invoice || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="agrl-section-label">Manifest Number</label>
                      <p className="agrl-section-value agrl-value-mono">{selectedGRN.delivery.manifestNumber || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="agrl-section-label">Delivery Date</label>
                      <p className="agrl-section-value">{formatDate(selectedGRN.delivery.date)}</p>
                    </div>
                    <div>
                      <label className="agrl-section-label">Vehicle</label>
                      <p className="agrl-section-value">{selectedGRN.delivery.vehicleNumber || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="agrl-section-label">Driver Name</label>
                      <p className="agrl-section-value">{selectedGRN.delivery.driverName || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="agrl-section-label">Driver Contact</label>
                      <p className="agrl-section-value">{selectedGRN.delivery.driverContact || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="agrl-section-label">LR Number</label>
                      <p className="agrl-section-value agrl-value-mono">{selectedGRN.delivery.lrNumber || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="agrl-section-label">Transport</label>
                      <p className="agrl-section-value">{selectedGRN.delivery.transportCompany || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="agrl-section-label">Received By</label>
                      <p className="agrl-section-value">{selectedGRN.delivery.receivedBy || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="agrl-section-label">Warehouse</label>
                      <p className="agrl-section-value">{selectedGRN.delivery.warehouse}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Received Items Table */}
              <div className="agrl-section">
                <h3 className="agrl-section-title agrl-section-title-amber">
                  Received Items
                </h3>
                <div className="agrl-table-wrapper">
                  <table className="agrl-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Description</th>
                        <th>HSN</th>
                        <th className="agrl-text-center">UOM</th>
                        <th className="agrl-text-right">Ordered</th>
                        <th className="agrl-text-right">Received</th>
                        <th className="agrl-text-right">Accepted</th>
                        <th className="agrl-text-right">Rejected</th>
                        <th>Batch/Lot</th>
                        <th>Mfg Date</th>
                        <th>Exp Date</th>
                        <th>Condition</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedGRN.items.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{item.description || '-'}</td>
                          <td className="agrl-value-mono agrl-text-small">{item.hsn || '-'}</td>
                          <td className="agrl-text-center">{item.uom || '-'}</td>
                          <td className="agrl-text-right">{item.ordered || '0'}</td>
                          <td className="agrl-text-right">{item.received || '0'}</td>
                          <td className="agrl-text-right agrl-text-green">{item.accepted || '0'}</td>
                          <td className="agrl-text-right agrl-text-red">{item.rejected || '0'}</td>
                          <td className="agrl-value-mono agrl-text-small">{item.batch || '-'}</td>
                          <td className="agrl-text-small">{item.mfgDate || '-'}</td>
                          <td className="agrl-text-small">{item.expDate || '-'}</td>
                          <td>
                            <span className={`agrl-item-badge agrl-badge-${item.condition?.toLowerCase().replace(/\s+/g, '-') || 'good'}`}>
                              {item.condition || 'Good'}
                            </span>
                          </td>
                          <td className="agrl-text-small">{item.remarks || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="1"></td>
                        <td className="agrl-text-left" colSpan="2">Totals:</td>
                        <td colSpan="1"></td>
                        <td className="agrl-text-right">{selectedGRN.items.reduce((sum, item) => sum + (parseFloat(item.ordered) || 0), 0)}</td>
                        <td className="agrl-text-right">{selectedGRN.items.reduce((sum, item) => sum + (parseFloat(item.received) || 0), 0)}</td>
                        <td className="agrl-text-right agrl-text-green">{selectedGRN.items.reduce((sum, item) => sum + (parseFloat(item.accepted) || 0), 0)}</td>
                        <td className="agrl-text-right agrl-text-red">{selectedGRN.items.reduce((sum, item) => sum + (parseFloat(item.rejected) || 0), 0)}</td>
                        <td colSpan="5"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Quality Inspection Checklist */}
              <div className="agrl-section">
                <h3 className="agrl-section-title agrl-section-title-green">
                  Quality Inspection Checklist
                </h3>
                <div className="agrl-section-card agrl-card-green">
                  <div className="agrl-section-grid agrl-grid-2">
                    {selectedGRN.qualityInspection.packagingIntact && (
                      <div className="agrl-checkbox-item">
                        <span className="agrl-check-icon agrl-checked">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5"></path>
                          </svg>
                        </span>
                        <label>Packaging Intact</label>
                      </div>
                    )}
                    {selectedGRN.qualityInspection.correctLabeling && (
                      <div className="agrl-checkbox-item">
                        <span className="agrl-check-icon agrl-checked">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5"></path>
                          </svg>
                        </span>
                        <label>Correct Labeling</label>
                      </div>
                    )}
                    {selectedGRN.qualityInspection.noVisibleDamage && (
                      <div className="agrl-checkbox-item">
                        <span className="agrl-check-icon agrl-checked">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5"></path>
                          </svg>
                        </span>
                        <label>No Visible Damage</label>
                      </div>
                    )}
                    {selectedGRN.qualityInspection.quantityMatches && (
                      <div className="agrl-checkbox-item">
                        <span className="agrl-check-icon agrl-checked">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5"></path>
                          </svg>
                        </span>
                        <label>Quantity Matches</label>
                      </div>
                    )}
                    {selectedGRN.qualityInspection.meetsQualityStandards && (
                      <div className="agrl-checkbox-item">
                        <span className="agrl-check-icon agrl-checked">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5"></path>
                          </svg>
                        </span>
                        <label>Quality Standards Met</label>
                      </div>
                    )}
                    {selectedGRN.qualityInspection.documentsComplete && (
                      <div className="agrl-checkbox-item">
                        <span className="agrl-check-icon agrl-checked">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5"></path>
                          </svg>
                        </span>
                        <label>Documents Complete</label>
                      </div>
                    )}
                    <div className="agrl-inspector-info">
                      <label className="agrl-section-label">Inspector</label>
                      <p className="agrl-section-value">{selectedGRN.qualityInspection.inspector} ({formatDate(selectedGRN.qualityInspection.inspectionDate)})</p>
                    </div>
                    {selectedGRN.qualityInspection.defectDescription && (
                      <div className="agrl-inspector-info">
                        <label className="agrl-section-label">Defect Description</label>
                        <p className="agrl-section-value agrl-text-red">{selectedGRN.qualityInspection.defectDescription}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Physical Verification */}
              <div className="agrl-section">
                <h3 className="agrl-section-title agrl-section-title-indigo">
                  Physical Verification
                </h3>
                <div className="agrl-section-card agrl-card-indigo">
                  <div className="agrl-section-grid agrl-grid-2">
                    <div>
                      <label className="agrl-section-label">Packages Expected / Received</label>
                      <p className="agrl-section-value">{selectedGRN.physicalVerification.packagesExpected} / {selectedGRN.physicalVerification.packagesReceived}</p>
                    </div>
                    <div>
                      <label className="agrl-section-label">Weight Expected / Received (kg)</label>
                      <p className="agrl-section-value">{selectedGRN.physicalVerification.weightExpected} / {selectedGRN.physicalVerification.weightReceived}</p>
                    </div>
                    {selectedGRN.physicalVerification.sealsVerified && selectedGRN.physicalVerification.sealNumbers && (
                      <div>
                        <label className="agrl-section-label">Seal Numbers</label>
                        <p className="agrl-section-value agrl-value-mono agrl-text-small">{selectedGRN.physicalVerification.sealNumbers}</p>
                      </div>
                    )}
                    <div className="agrl-checkbox-item">
                      <span className={`agrl-check-icon ${selectedGRN.physicalVerification.sealsVerified ? 'agrl-checked' : 'agrl-unchecked'}`}>
                        {selectedGRN.physicalVerification.sealsVerified ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5"></path>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                          </svg>
                        )}
                      </span>
                      <label>Seals Verified</label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Discrepancies & Issues */}
              {(selectedGRN.discrepancies && Object.keys(selectedGRN.discrepancies).length > 0) && (
                <div className="agrl-section">
                  <h3 className="agrl-section-title agrl-section-title-red">
                    Discrepancies & Issues
                  </h3>
                  <div className="agrl-section-card agrl-card-red">
                    <div className="agrl-discrepancies-list">
                      {selectedGRN.discrepancies.shortageDetected && selectedGRN.discrepancies.shortageDescription && (
                        <div className="agrl-discrepancy-item">
                          <label className="agrl-section-label agrl-text-red">Shortage Details</label>
                          <p className="agrl-section-value">{selectedGRN.discrepancies.shortageDescription}</p>
                        </div>
                      )}
                      {selectedGRN.discrepancies.damageDetected && selectedGRN.discrepancies.damageDescription && (
                        <div className="agrl-discrepancy-item">
                          <label className="agrl-section-label agrl-text-red">Damage Details</label>
                          <p className="agrl-section-value">{selectedGRN.discrepancies.damageDescription}</p>
                        </div>
                      )}
                      {selectedGRN.discrepancies.qualityIssue && selectedGRN.discrepancies.qualityIssueDescription && (
                        <div className="agrl-discrepancy-item">
                          <label className="agrl-section-label agrl-text-red">Quality Issue</label>
                          <p className="agrl-section-value">{selectedGRN.discrepancies.qualityIssueDescription}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Notes */}
              {selectedGRN.additionalNotes && (
                <div className="agrl-section">
                  <h3 className="agrl-section-title">
                    <span className="agrl-section-indicator agrl-indicator-gray"></span>
                    Additional Notes
                  </h3>
                  <div className="agrl-section-card">
                    <p className="agrl-section-value">{selectedGRN.additionalNotes}</p>
                  </div>
                </div>
              )}

              {/* Approval Details */}
              {(selectedGRN.status === 'Approved' || selectedGRN.status === 'Partially Approved') && selectedGRN.approvalDetails && selectedGRN.approvalDetails.approvedBy && (
                <div className="agrl-section">
                  <h3 className="agrl-section-title">
                    <span className={`agrl-section-indicator ${selectedGRN.status === 'Approved' ? 'agrl-indicator-green' : 'agrl-indicator-amber'}`}></span>
                    {selectedGRN.status === 'Approved' ? 'Approval Details' : 'Partial Approval Details'}
                  </h3>
                  <div className="agrl-section-card">
                    <div className="agrl-section-grid agrl-grid-4">
                      <div>
                        <label className="agrl-section-label">Approved By</label>
                        <p className="agrl-section-value">{selectedGRN.approvalDetails.approvedBy}</p>
                      </div>
                      {selectedGRN.approvalDetails.approvedByDesignation && (
                        <div>
                          <label className="agrl-section-label">Designation</label>
                          <p className="agrl-section-value">{selectedGRN.approvalDetails.approvedByDesignation}</p>
                        </div>
                      )}
                      {selectedGRN.approvalDetails.approvedDate && (
                        <div>
                          <label className="agrl-section-label">Approval Date</label>
                          <p className="agrl-section-value">{formatDate(selectedGRN.approvalDetails.approvedDate)}</p>
                        </div>
                      )}
                      {selectedGRN.approvalDetails.followUpAction && (
                        <div>
                          <label className="agrl-section-label">Follow-up Action</label>
                          <p className="agrl-section-value">{getFollowUpActionLabel(selectedGRN.approvalDetails.followUpAction)}</p>
                        </div>
                      )}
                      {selectedGRN.approvalDetails.approvalNotes && (
                        <div style={{ gridColumn: 'span 4' }}>
                          <label className="agrl-section-label">Approval Notes</label>
                          <p className="agrl-section-value">{selectedGRN.approvalDetails.approvalNotes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Rejection Details */}
              {selectedGRN.status === 'Rejected' && selectedGRN.rejectionDetails && selectedGRN.rejectionDetails.rejectedBy && (
                <div className="agrl-section">
                  <h3 className="agrl-section-title">
                    <span className="agrl-section-indicator agrl-indicator-red"></span>
                    Rejection Details
                  </h3>
                  <div className="agrl-section-card">
                    <div className="agrl-section-grid agrl-grid-4">
                      <div>
                        <label className="agrl-section-label">Rejected By</label>
                        <p className="agrl-section-value">{selectedGRN.rejectionDetails.rejectedBy}</p>
                      </div>
                      {selectedGRN.rejectionDetails.rejectedByDesignation && (
                        <div>
                          <label className="agrl-section-label">Designation</label>
                          <p className="agrl-section-value">{selectedGRN.rejectionDetails.rejectedByDesignation}</p>
                        </div>
                      )}
                      {selectedGRN.rejectionDetails.rejectedDate && (
                        <div>
                          <label className="agrl-section-label">Rejection Date</label>
                          <p className="agrl-section-value">{formatDate(selectedGRN.rejectionDetails.rejectedDate)}</p>
                        </div>
                      )}
                      {selectedGRN.rejectionDetails.followUpAction && (
                        <div>
                          <label className="agrl-section-label">Follow-up Action</label>
                          <p className="agrl-section-value">{getFollowUpActionLabel(selectedGRN.rejectionDetails.followUpAction)}</p>
                        </div>
                      )}
                      {selectedGRN.rejectionDetails.rejectionReason && (
                        <div style={{ gridColumn: 'span 4' }}>
                          <label className="agrl-section-label" style={{ color: '#DC2626' }}>Rejection Reason</label>
                          <p className="agrl-section-value">{selectedGRN.rejectionDetails.rejectionReason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="agrl-modal-footer">
              {selectedGRN.status === 'Pending Approval' && (
                <button className="agrl-btn agrl-btn-primary" onClick={() => handleReviewAccept(selectedGRN.id)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                    <path d="m9 11 3 3L22 4"></path>
                  </svg>
                  Review & Accept
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* GRN Acceptance Decision Modal */}
      {isAcceptModalOpen && acceptGRN && (
        <div className="agrl-accept-modal-overlay" onClick={handleCloseAcceptModal}>
          <div
            className="agrl-accept-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="agrl-accept-modal-title"
            aria-describedby="agrl-accept-modal-description"
            tabIndex="-1"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="agrl-accept-modal-close" 
              onClick={handleCloseAcceptModal}
              aria-label="Close acceptance modal"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>

            <div className="agrl-accept-modal-header">
              <div>
                <h2 id="agrl-accept-modal-title" className="agrl-accept-modal-title">GRN Acceptance Decision - {acceptGRN.grnNumber}</h2>
                <p id="agrl-accept-modal-description" className="agrl-accept-modal-subtitle">Review and provide your acceptance decision for this GRN</p>
              </div>
            </div>

            <div className="agrl-accept-modal-content">
              {/* Summary Info */}
              <div className="agrl-accept-summary">
                <div className="agrl-accept-summary-grid">
                  <div>
                    <label className="agrl-accept-summary-label">PO Number</label>
                    <p className="agrl-accept-summary-value agrl-value-mono">{acceptGRN.poNumber}</p>
                  </div>
                  <div>
                    <label className="agrl-accept-summary-label">Total Accepted</label>
                    <p className="agrl-accept-summary-value agrl-text-green">{acceptGRN.totalAccepted}</p>
                  </div>
                  <div>
                    <label className="agrl-accept-summary-label">Total Rejected</label>
                    <p className="agrl-accept-summary-value agrl-text-red">{acceptGRN.totalRejected}</p>
                  </div>
                  <div>
                    <label className="agrl-accept-summary-label">Supplier</label>
                    <p className="agrl-accept-summary-value agrl-text-truncate">{acceptGRN.supplier}</p>
                  </div>
                </div>
              </div>

              {/* Acceptance Decision */}
              <div className="agrl-accept-decision-section">
                <label className="agrl-accept-decision-label">
                  Acceptance Decision <span className="agrl-required">*</span>
                </label>
                <div className="agrl-accept-radio-group">
                  <div 
                    className={`agrl-accept-radio-option ${acceptanceForm.decision === 'APPROVE' ? 'agrl-radio-selected' : ''}`}
                    onClick={() => handleAcceptanceFormChange('decision', 'APPROVE')}
                  >
                    <input
                      type="radio"
                      name="acceptanceDecision"
                      id="approve"
                      value="APPROVE"
                      checked={acceptanceForm.decision === 'APPROVE'}
                      onChange={() => handleAcceptanceFormChange('decision', 'APPROVE')}
                      className="agrl-radio-input"
                    />
                    <label htmlFor="approve" className="agrl-radio-label">
                      <div className="agrl-radio-content">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="agrl-radio-icon agrl-icon-green">
                          <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                          <path d="m9 11 3 3L22 4"></path>
                        </svg>
                        <div>
                          <p className="agrl-radio-title">Approve GRN</p>
                          <p className="agrl-radio-description">Accept all received items and update inventory</p>
                        </div>
                      </div>
                    </label>
                  </div>

                  <div 
                    className={`agrl-accept-radio-option ${acceptanceForm.decision === 'PARTIALLY_ACCEPT' ? 'agrl-radio-selected' : ''}`}
                    onClick={() => handleAcceptanceFormChange('decision', 'PARTIALLY_ACCEPT')}
                  >
                    <input
                      type="radio"
                      name="acceptanceDecision"
                      id="partial"
                      value="PARTIALLY_ACCEPT"
                      checked={acceptanceForm.decision === 'PARTIALLY_ACCEPT'}
                      onChange={() => handleAcceptanceFormChange('decision', 'PARTIALLY_ACCEPT')}
                      className="agrl-radio-input"
                    />
                    <label htmlFor="partial" className="agrl-radio-label">
                      <div className="agrl-radio-content">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="agrl-radio-icon agrl-icon-orange">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" x2="12" y1="8" y2="12"></line>
                          <line x1="12" x2="12.01" y1="16" y2="16"></line>
                        </svg>
                        <div>
                          <p className="agrl-radio-title">Partially Accept</p>
                          <p className="agrl-radio-description">Accept some items and reject others</p>
                        </div>
                      </div>
                    </label>
                  </div>

                  <div 
                    className={`agrl-accept-radio-option ${acceptanceForm.decision === 'REJECT' ? 'agrl-radio-selected' : ''}`}
                    onClick={() => handleAcceptanceFormChange('decision', 'REJECT')}
                  >
                    <input
                      type="radio"
                      name="acceptanceDecision"
                      id="reject"
                      value="REJECT"
                      checked={acceptanceForm.decision === 'REJECT'}
                      onChange={() => handleAcceptanceFormChange('decision', 'REJECT')}
                      className="agrl-radio-input"
                    />
                    <label htmlFor="reject" className="agrl-radio-label">
                      <div className="agrl-radio-content">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="agrl-radio-icon agrl-icon-red">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="m15 9-6 6"></path>
                          <path d="m9 9 6 6"></path>
                        </svg>
                        <div>
                          <p className="agrl-radio-title">Reject GRN</p>
                          <p className="agrl-radio-description">Reject entire shipment and notify supplier</p>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Approver Details */}
              <div className="agrl-accept-approver-section">
                <div className="agrl-accept-approver-grid">
                  <div>
                    <label className="agrl-accept-field-label">
                      {acceptanceForm.decision === 'REJECT' ? 'Rejector Name' : 'Approver Name'} <span className="agrl-required">*</span>
                    </label>
                    <input
                      type="text"
                      className="agrl-accept-input"
                      placeholder="Your full name"
                      value={acceptanceForm.approverName}
                      onChange={(e) => handleAcceptanceFormChange('approverName', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="agrl-accept-field-label">
                      Designation <span className="agrl-required">*</span>
                    </label>
                    <input
                      type="text"
                      className="agrl-accept-input"
                      placeholder="e.g., Warehouse Manager"
                      value={acceptanceForm.designation}
                      onChange={(e) => handleAcceptanceFormChange('designation', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="agrl-accept-field-label">
                      {acceptanceForm.decision === 'REJECT' ? 'Rejection Date' : 'Approval Date'}
                    </label>
                    <input
                      type="date"
                      className="agrl-accept-input"
                      value={acceptanceForm.approvalDate}
                      onChange={(e) => handleAcceptanceFormChange('approvalDate', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Approval Notes - Only shown when Approve GRN is selected */}
              {acceptanceForm.decision === 'APPROVE' && (
                <div className="agrl-accept-notes-section">
                  <label className="agrl-accept-field-label">
                    Approval Notes <span className="agrl-required">*</span>
                  </label>
                  <textarea
                    className="agrl-accept-textarea"
                    placeholder="Enter approval notes or comments..."
                    rows="3"
                    value={acceptanceForm.approvalNotes}
                    onChange={(e) => handleAcceptanceFormChange('approvalNotes', e.target.value)}
                    required
                  />
                </div>
              )}

              {/* Follow-up Action Required - Only shown when Partially Accept is selected */}
              {acceptanceForm.decision === 'PARTIALLY_ACCEPT' && (
                <div className="agrl-accept-followup-section">
                  <label className="agrl-accept-field-label">
                    Follow-up Action Required <span className="agrl-required">*</span>
                  </label>
                  <select
                    className="agrl-accept-select"
                    value={acceptanceForm.followUpAction}
                    onChange={(e) => handleAcceptanceFormChange('followUpAction', e.target.value)}
                    required
                  >
                    {followUpActionOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Rejection Reason and Follow-up Action - Only shown when Reject GRN is selected */}
              {acceptanceForm.decision === 'REJECT' && (
                <>
                  <div className="agrl-accept-rejection-section">
                    <label className="agrl-accept-field-label">
                      Rejection Reason <span className="agrl-required">*</span>
                    </label>
                    <textarea
                      className="agrl-accept-textarea"
                      placeholder="Explain why this GRN is being rejected..."
                      rows="3"
                      value={acceptanceForm.rejectionReason}
                      onChange={(e) => handleAcceptanceFormChange('rejectionReason', e.target.value)}
                      required
                    />
                  </div>
                  <div className="agrl-accept-followup-section">
                    <label className="agrl-accept-field-label">
                      Follow-up Action Required <span className="agrl-required">*</span>
                    </label>
                    <select
                      className="agrl-accept-select"
                      value={acceptanceForm.followUpAction}
                      onChange={(e) => handleAcceptanceFormChange('followUpAction', e.target.value)}
                      required
                    >
                      {followUpActionOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            <div className="agrl-accept-modal-footer">
              <button 
                className="agrl-btn agrl-btn-secondary" 
                onClick={handleCloseAcceptModal}
              >
                Cancel
              </button>
              <button 
                className="agrl-btn agrl-btn-primary" 
                onClick={handleSubmitDecision}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Decision'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

